import { useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import type { DirectoryResponse } from "@/client/ContentVaultApiClient/models";
import { createAuthenticatedApiClient } from "@/client/utils/apiClient";
import { authClient } from "@/client/AuthClient/AuthClient";
import { logger } from "@/lib/logger";
import { PinnedDirectoriesContext } from "./pinned-directories-context";

export function PinnedDirectoriesProvider({ children }: { children: ReactNode }) {
    const [pinnedDirectories, setPinnedDirectories] = useState<DirectoryResponse[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const userSession = authClient.useSession();
    const sessionToken = userSession.data?.session?.token;
    const userId = userSession.data?.user?.id ?? null;
    const organisationId = userSession.data?.session?.activeOrganizationId ?? null;
    // Pins belong to a user within an organisation. The session is polled and refetched on
    // window focus, so key on who is signed in and where rather than on the session object -
    // a routine refetch then leaves the pins alone.
    const scope = userId ? `${userId}:${organisationId ?? ""}` : null;

    // Drop the previous user's (or organisation's) pins as soon as the scope changes, during
    // render so they are never painted for the next one.
    const [syncedScope, setSyncedScope] = useState(scope);
    if (syncedScope !== scope) {
        setSyncedScope(scope);
        setPinnedDirectories([]);
        setIsLoading(false);
    }

    // The latest token and scope, read by refreshes that outlive the render that started them.
    const sessionTokenRef = useRef(sessionToken);
    const scopeRef = useRef(scope);
    useEffect(() => {
        sessionTokenRef.current = sessionToken;
        scopeRef.current = scope;
    }, [sessionToken, scope]);

    // Numbers each refresh. Refreshes overlap (a pin toggle, then an archive) and their
    // responses can land out of order, so only the latest one may set the pins.
    const requestSequenceRef = useRef(0);

    const refreshPinnedDirectories = useCallback(async () => {
        const token = sessionTokenRef.current;
        const requestScope = scopeRef.current;
        if (!token || !requestScope) {
            logger.warn("No session token available");
            return;
        }

        const request = ++requestSequenceRef.current;
        // Superseded by a later refresh, or signed out / switched organisation meanwhile.
        const isCurrent = () => requestSequenceRef.current === request && scopeRef.current === requestScope;

        setIsLoading(true);
        try {
            const apiClient = createAuthenticatedApiClient(token);

            const directories = await apiClient.directories.pinned.get();
            if (!isCurrent()) return;
            setPinnedDirectories(directories?.items ?? []);
        } catch (error) {
            if (!isCurrent()) return;
            logger.error("Failed to fetch pinned directories", error);
        } finally {
            if (isCurrent()) {
                setIsLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        if (!scope) return;

        void refreshPinnedDirectories();
    }, [scope, refreshPinnedDirectories]);

    return (
        <PinnedDirectoriesContext.Provider
            value={{ pinnedDirectories, refreshPinnedDirectories, isLoading }}
        >
            {children}
        </PinnedDirectoriesContext.Provider>
    );
}
